import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronRight,
  Monitor,
  Smartphone,
  Tablet,
  MapPin,
  Clock,
} from 'lucide-react';
import { api } from '../lib/api';
import type { VisitSummary, RawPageSeconds } from '../lib/types';
import { duration, formatDate, timeAgo } from '../lib/format';
import { VisitBadges } from './VisitBadges';

/**
 * The DocSend-style activity feed: one row per visit — identity, link,
 * device/location, dwell time, completion — expandable into a per-page
 * time-spent chart with page-thumbnail hover previews.
 */
export function ActivityList({
  visits,
  raw,
  docTitles,
  showLink = false,
  showProject = false,
  emptyText = 'No visits yet.',
}: {
  visits: VisitSummary[];
  raw: RawPageSeconds[];
  docTitles: Record<string, string>;
  showLink?: boolean;
  showProject?: boolean;
  emptyText?: string;
}) {
  const rawById = new Map(raw.map((r) => [r.visitId, r.pageSeconds]));
  if (visits.length === 0) {
    return (
      <p className="border border-dashed border-white/20 p-8 text-center text-sm text-soft">
        {emptyText}
      </p>
    );
  }
  return (
    <div className="space-y-2">
      {visits.map((v) => (
        <VisitRow
          key={v.visitId}
          visit={v}
          pageSeconds={rawById.get(v.visitId) ?? {}}
          docTitles={docTitles}
          showLink={showLink}
          showProject={showProject}
        />
      ))}
    </div>
  );
}

function DeviceIcon({ type }: { type: string }) {
  const cls = 'h-3 w-3';
  if (type === 'mobile') return <Smartphone className={cls} />;
  if (type === 'tablet') return <Tablet className={cls} />;
  return <Monitor className={cls} />;
}

function VisitRow({
  visit,
  pageSeconds,
  docTitles,
  showLink,
  showProject,
}: {
  visit: VisitSummary;
  pageSeconds: Record<string, number>;
  docTitles: Record<string, string>;
  showLink: boolean;
  showProject: boolean;
}) {
  const [open, setOpen] = useState(false);
  const maxCompletion = Math.max(
    0,
    ...visit.docsViewed.map((d) => d.completionPct ?? 0),
  );

  return (
    <div className="rw-card">
      <button
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 p-3 text-left"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-soft" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-soft" />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="truncate text-sm font-medium text-ink">
              {visit.email ?? 'Anonymous'}
            </span>
            {showLink && visit.linkLabel && (
              <span className="rounded-sm bg-paper px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-mid">
                {visit.linkLabel}
              </span>
            )}
            {showProject && visit.projectName && (
              <span className="rounded-sm bg-paper px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-mid">
                {visit.projectName}
              </span>
            )}
            <VisitBadges visit={visit} />
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-mid">
            <span title={formatDate(visit.startedAt)}>{timeAgo(visit.startedAt)}</span>
            <span className="flex items-center gap-1 font-medium text-charcoal">
              <Clock className="h-3 w-3" /> {duration(visit.totalSeconds)}
            </span>
            <span className="flex items-center gap-1">
              <DeviceIcon type={visit.device.type} /> {visit.device.browser} ·{' '}
              {visit.device.os}
            </span>
            {visit.geo?.city && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {visit.geo.city}
                {visit.geo.region ? `, ${visit.geo.region}` : ''}
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {visit.docsViewed.length > 0 && (
            <div className="hidden text-right text-xs text-mid sm:block">
              {visit.docsViewed.slice(0, 2).map((d) => (
                <div key={`${d.docId}#${d.version}`} className="truncate">
                  <span className="text-charcoal">
                    {docTitles[d.docId] ?? 'Document'}
                  </span>{' '}
                  {d.completionPct !== null && (
                    <span className="font-semibold text-accent">
                      {d.completionPct}%
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          {maxCompletion > 0 && (
            <CompletionRing pct={maxCompletion} />
          )}
        </div>
      </button>
      {open && (
        <div className="border-t border-line/40 p-4">
          {visit.docsViewed.map((d) => (
            <VisitPageChart
              key={`${d.docId}#${d.version}`}
              projectId={visit.projectId ?? null}
              docId={d.docId}
              version={d.version}
              title={`${docTitles[d.docId] ?? 'Document'} — v${d.version} · ${duration(d.seconds)} · ${
                d.completionPct !== null ? `${d.completionPct}% viewed` : `${d.pagesSeen} pages`
              }`}
              pageCount={d.pageCount}
              pageSeconds={pageSeconds}
            />
          ))}
          {visit.downloads.length > 0 && (
            <div className="mt-1 text-xs text-mid">
              Downloads:{' '}
              {visit.downloads
                .map((d) => docTitles[d.docId] ?? d.docId)
                .join(', ')}
            </div>
          )}
          {visit.docsViewed.length === 0 && visit.downloads.length === 0 && (
            <p className="text-xs text-mid">
              Opened the link but no page time was recorded.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/** DocSend-style completion donut. */
function CompletionRing({ pct }: { pct: number }) {
  const r = 13;
  const c = 2 * Math.PI * r;
  return (
    <svg width="34" height="34" viewBox="0 0 34 34" className="shrink-0 -rotate-90">
      <circle cx="17" cy="17" r={r} fill="none" stroke="#e8e8e5" strokeWidth="4" />
      <circle
        cx="17"
        cy="17"
        r={r}
        fill="none"
        stroke="#8b1a10"
        strokeWidth="4"
        strokeDasharray={`${(pct / 100) * c} ${c}`}
        strokeLinecap="butt"
      />
      <text
        x="17"
        y="17"
        transform="rotate(90 17 17)"
        textAnchor="middle"
        dominantBaseline="central"
        fontSize="9"
        fontWeight="600"
        fill="#2c2c2c"
      >
        {pct}
      </text>
    </svg>
  );
}

/** Per-page dwell bars with a page-thumbnail hover preview. */
export function VisitPageChart({
  projectId,
  docId,
  version,
  title,
  pageCount,
  pageSeconds,
}: {
  projectId: string | null;
  docId: string;
  version: number;
  title: string;
  pageCount: number | null;
  pageSeconds: Record<string, number>;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const thumbs = useQuery({
    queryKey: ['thumbs', projectId, docId, version],
    queryFn: () => api.docThumbs(projectId!, docId, version),
    enabled: !!projectId,
    staleTime: 30 * 60 * 1000,
    retry: false,
  });

  const prefix = `${docId}#v${String(version).padStart(3, '0')}#p`;
  const perPage = new Map<number, number>();
  for (const [k, s] of Object.entries(pageSeconds)) {
    if (k.startsWith(prefix)) perPage.set(Number(k.slice(prefix.length)), s);
  }
  const n = pageCount ?? Math.max(0, ...perPage.keys());
  if (!n) return null;
  const max = Math.max(1, ...perPage.values());

  return (
    <div className="relative mb-5">
      <div className="mb-1.5 text-xs font-medium text-charcoal">{title}</div>
      {hover !== null && (
        <div className="pointer-events-none absolute -top-2 left-1/2 z-10 -translate-x-1/2 -translate-y-full border border-line bg-white p-1.5 shadow-lg">
          {thumbs.data?.thumbs[hover - 1] && (
            <img
              src={thumbs.data.thumbs[hover - 1]}
              alt=""
              className="mb-1 w-28 border border-line/50"
            />
          )}
          <div className="text-center text-[10px] font-medium text-charcoal">
            Page {hover} · {duration(perPage.get(hover) ?? 0)}
          </div>
        </div>
      )}
      <div className="flex items-end gap-[3px] bg-paper p-2" style={{ height: 72 }}>
        {Array.from({ length: n }, (_, i) => {
          const s = perPage.get(i + 1) ?? 0;
          return (
            <div
              key={i}
              onMouseEnter={() => setHover(i + 1)}
              onMouseLeave={() => setHover((h) => (h === i + 1 ? null : h))}
              className="min-w-[8px] flex-1 cursor-pointer"
              style={{
                height: `${Math.max(5, (s / max) * 100)}%`,
                backgroundColor: s > 0 ? '#8b1a10' : '#d9d9d5',
                opacity: s > 0 ? 0.45 + 0.55 * (s / max) : 1,
              }}
            />
          );
        })}
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-soft">
        <span>Page 1</span>
        <span>Page {n}</span>
      </div>
    </div>
  );
}
